import * as React from 'react'
import Navbar from './Navbar'
import Predictions from './Predictions'
import {useEffect, useRef, useState} from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import {
    Typography, Button, Box, Alert, Card, CardContent, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import { apiUrl, authenticatedFetch } from '../utils/api'
import {
    applyLeagueCompetitionToState,
    competitionSlugForLeagueView,
    competitionSlugFromLeagueUrl,
    fetchLeagueById,
    getLeagueFromSnapshot,
    removeLeagueFromSnapshot,
} from '../utils/leaguesSnapshot'
import AdminPanel from './AdminPanel'

/** True once kickoff has passed (or fixture marked completed). ESPN may publish 0-0 before kickoff. */
function fixtureHasStarted(fixture) {
    if (!fixture) return false
    if (fixture.is_completed) return true
    const kickoff = fixture.fixture_date || fixture.date
    if (!kickoff) return false
    return Date.now() >= new Date(kickoff).getTime()
}

function Members() {
    const location = useLocation()
    const history = useHistory()
    const leagueOpsAbortRef = useRef(null)
    const [leagueId, setLeagueId] = useState(null)
    const [leaderboard, setLeaderboard] = useState([])
    const [leaderboardScope, setLeaderboardScope] = useState('full_season')
    const [leaderboardCurrentRound, setLeaderboardCurrentRound] = useState(null)
    const [loadingLeaderboard, setLoadingLeaderboard] = useState(false)
    const [filteredFixtures, setFilteredFixtures] = useState([])
    const [availableRounds, setAvailableRounds] = useState([])
    const [gameWeek, setGameWeek] = useState('')
    const [syncing, setSyncing] = useState(false)
    const [syncMessage, setSyncMessage] = useState(null)
    const [loadingFixtures, setLoadingFixtures] = useState(false)
    const [predictions, setPredictions] = useState([])
    const [loadingPredictions, setLoadingPredictions] = useState(false)
    // Admin: current user and league with members/roles
    const [currentUser, setCurrentUser] = useState(null)
    const [leagueDetail, setLeagueDetail] = useState(null)
    const [deleteLeagueDialog, setDeleteLeagueDialog] = useState(false)
    const [deletingLeague, setDeletingLeague] = useState(false)
    const [deleteLeagueError, setDeleteLeagueError] = useState(null)
    const [removingMemberId, setRemovingMemberId] = useState(null)
    const [updatingRoleId, setUpdatingRoleId] = useState(null)
    const [adminMemberPredictions, setAdminMemberPredictions] = useState([])
    const [adminSelectedMemberId, setAdminSelectedMemberId] = useState('')
    const [loadingMemberPredictions, setLoadingMemberPredictions] = useState(false)
    const [editPredictionDialog, setEditPredictionDialog] = useState(null) // { gameId, home_team, away_team, home_team_score, away_team_score }
    const [addPredictionDialog, setAddPredictionDialog] = useState(null) // { home_team, away_team, home_team_score, away_team_score } for missed pick
    const [adminPredictionRound, setAdminPredictionRound] = useState('')
    const [adminFixturesForRound, setAdminFixturesForRound] = useState([])
    const [loadingAdminFixtures, setLoadingAdminFixtures] = useState(false)
    const [roundOverrideDialog, setRoundOverrideDialog] = useState(null) // { fixtureId, home_team, away_team, current_round, new_round, manual_round_override }
    const [savingRoundOverride, setSavingRoundOverride] = useState(false)
    const [backfillDialogOpen, setBackfillDialogOpen] = useState(false)
    const [backfillJson, setBackfillJson] = useState('')
    const [backfillMessage, setBackfillMessage] = useState(null)
    const [newSeasonDialogOpen, setNewSeasonDialogOpen] = useState(false)
    const [newSeasonSyncFixtures, setNewSeasonSyncFixtures] = useState(true)
    const [startingNewSeason, setStartingNewSeason] = useState(false)
    const [plStandings, setPlStandings] = useState(null)
    const [loadingStandings, setLoadingStandings] = useState(false)
    const [standingsError, setStandingsError] = useState(null)
    const [competitions, setCompetitions] = useState([])
    const [selectedCompetition, setSelectedCompetition] = useState(
        () => competitionSlugFromLeagueUrl(location.search) || 'eng.1'
    )
    const [displayNameDialogOpen, setDisplayNameDialogOpen] = useState(false)
    const [displayNameDraft, setDisplayNameDraft] = useState('')
    const [displayNameError, setDisplayNameError] = useState(null)
    const [displayNameSaving, setDisplayNameSaving] = useState(false)
    const [adminPanelOpen, setAdminPanelOpen] = useState(false)

    // Prefer backend is_admin flag; fallback to member role or league creator
    const isAdmin = leagueDetail?.is_admin === true ||
        leagueDetail?.members?.some(m => Number(m.id) === Number(currentUser?.id) && m.role === 'admin') ||
        (leagueDetail?.created_by != null && currentUser?.id != null && Number(leagueDetail.created_by) === Number(currentUser.id)) ||
        false
    const leagueMembers = leagueDetail?.members ?? []
    // Snapshot + league detail so API calls use the right competition before /leagues returns
    const effectiveCompetition = competitionSlugForLeagueView(leagueId, leagueDetail, selectedCompetition)
    const isWorldCup = effectiveCompetition === 'fifa.world'
    const weekOrDayLabel = isWorldCup ? 'Day' : 'Game Week'
    const weekOrDayTitle = (num) => isWorldCup ? `Day ${num}` : `Week ${num}`

    // Get league_id from URL query params
    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const leagueParam = params.get('league')
        if (leagueParam && !Number.isNaN(parseInt(leagueParam, 10))) {
            setLeagueId(parseInt(leagueParam, 10))
            const slug = competitionSlugFromLeagueUrl(location.search)
            if (slug) setSelectedCompetition(slug)
        } else {
            setLeagueId(null)
        }
    }, [location.search, history])

    // Fetch current user
    useEffect(() => {
        authenticatedFetch('/api/v1/authorized')
            .then(res => res.ok ? res.json() : null)
            .then(user => setCurrentUser(user))
            .catch(() => setCurrentUser(null))
    }, [])

    // Hydrate league from snapshot, then refresh from API (members/roles + silent score refresh for admins)
    useEffect(() => {
        if (!leagueId) {
            setLeagueDetail(null)
            return
        }
        leagueOpsAbortRef.current?.abort()
        const abort = new AbortController()
        leagueOpsAbortRef.current = abort
        const { signal } = abort
        const cached = getLeagueFromSnapshot(leagueId)
        if (cached) {
            setLeagueDetail(cached)
            applyLeagueCompetitionToState(cached, setSelectedCompetition)
        } else {
            setLeagueDetail(null)
        }
        fetchLeagueById(authenticatedFetch, leagueId, { signal })
            .then((league) => {
                if (signal.aborted) return
                setLeagueDetail(league || null)
                if (league?.competition_slug) {
                    setSelectedCompetition(league.competition_slug)
                } else if (league) {
                    setSelectedCompetition('eng.1')
                }
                if (league?.is_admin) {
                    const comp = league.competition_slug || competitionSlugForLeagueView(leagueId, league, selectedCompetition) || 'eng.1'
                    authenticatedFetch('/api/v1/fixtures/sync-scores?' + new URLSearchParams({
                        competition: comp,
                        scores_only: 'true',
                    }).toString(), {
                        method: 'GET',
                        signal,
                    })
                        .then(res => res.ok ? res.json() : res.json().then(err => { throw new Error(err.error || 'Sync failed') }))
                        .then(data => {
                            if (signal.aborted) return
                            if (data?.error) throw new Error(data.error)
                            return authenticatedFetch('/api/v1/predictions/check-results', { method: 'POST', signal })
                        })
                        .then(() => {
                            if (signal.aborted) return
                            getPredictions()
                            fetchLeaderboard()
                            getAvailableRounds()
                            loadCurrentRoundAndFixtures()
                        })
                        .catch((err) => {
                            if (err?.name === 'AbortError') return
                        })
                }
            })
            .catch((err) => {
                if (err?.name === 'AbortError') return
                if (!cached) setLeagueDetail(null)
                if (err?.status === 403 || err?.status === 404) {
                    history.push('/leagues')
                }
            })
        fetchLeaderboard()
        return () => {
            abort.abort()
            if (leagueOpsAbortRef.current === abort) leagueOpsAbortRef.current = null
        }
    }, [leagueId, history])

    // Fetch supported competitions (leagues) for predictions
    useEffect(() => {
        fetch(apiUrl('/api/v1/competitions'))
            .then(res => res.ok ? res.json() : { competitions: [] })
            .then(data => setCompetitions(data.competitions || []))
            .catch(() => setCompetitions([]))
    }, [])

    // Fetch league standings for current competition (all except World Cup)
    const [standingsTitle, setStandingsTitle] = useState('')
    useEffect(() => {
        if (effectiveCompetition === 'fifa.world' || !effectiveCompetition) {
            setPlStandings(null)
            setStandingsError(null)
            setStandingsTitle('')
            return
        }
        setLoadingStandings(true)
        setStandingsError(null)
        setStandingsTitle('')
        const url = apiUrl('/api/v1/standings') + '?competition=' + encodeURIComponent(effectiveCompetition)
        fetch(url)
            .then(res => res.ok ? res.json() : res.json().then(err => { throw new Error(err.error || 'Failed to load standings') }))
            .then(data => {
                setPlStandings(data.standings || [])
                setStandingsTitle(data.competition_name || '')
            })
            .catch(err => {
                setStandingsError(err.message || 'Could not load league table')
                setPlStandings([])
            })
            .finally(() => setLoadingStandings(false))
    }, [effectiveCompetition])

    // On league page load: run check-results once competition is known. (Admin sync runs in league-fetch effect.)
    useEffect(() => {
        if (!leagueId || !effectiveCompetition) return
        const abort = new AbortController()
        authenticatedFetch('/api/v1/predictions/check-results', { method: 'POST', signal: abort.signal })
            .then(() => {
                if (abort.signal.aborted) return
                getPredictions()
                fetchLeaderboard()
            })
            .catch((err) => {
                if (err?.name === 'AbortError') return
            })
        return () => abort.abort()
    }, [leagueId, effectiveCompetition])

    // Refetch admin member predictions when member or round changes (ensures mobile Select always loads correct member's picks)
    useEffect(() => {
        if (!leagueId || !isAdmin) return
        const memberId = adminSelectedMemberId && String(adminSelectedMemberId).trim() ? Number(adminSelectedMemberId) : null
        if (memberId) handleFetchMemberPredictions(memberId)
        else setAdminMemberPredictions([])
        const round = adminPredictionRound != null && String(adminPredictionRound).trim() !== '' ? Number(adminPredictionRound) : null
        if (round != null && !Number.isNaN(round)) getAdminFixturesForRound(round)
    }, [leagueId, isAdmin, adminSelectedMemberId, adminPredictionRound])

    function getAvailableRounds() {
        const q = effectiveCompetition ? `?competition=${encodeURIComponent(effectiveCompetition)}` : ''
        fetch(apiUrl('/api/v1/fixtures/rounds') + q)
        .then(response => {
            if (!response.ok) {
                throw new Error('Failed to fetch rounds')
            }
            return response.json()
        })
        .then(data => {
            const rounds = data.rounds || []
            setAvailableRounds(rounds)
            return rounds
        })
        .catch(error => {
            console.error('Error fetching rounds:', error)
            setAvailableRounds([])
            return []
        })
    }

    /** Load the default game week: the lowest week that has not fully completed yet (week 1
     *  before GW1, then week 2 after GW1, etc.). */
    function loadCurrentRoundAndFixtures() {
        const compQ = effectiveCompetition ? `&competition=${encodeURIComponent(effectiveCompetition)}` : ''
        fetch(apiUrl('/api/v1/fixtures/current-round') + '?t=' + Date.now() + compQ, { cache: 'no-store' })
        .then(response => response.ok ? response.json() : { round: null })
        .then(data => {
            const round = data.round != null ? String(data.round) : ''
            setGameWeek(round)
            if (round) {
                getFixtures(parseInt(round, 10))
            } else {
                setFilteredFixtures([])
            }
        })
        .catch(() => {
            setGameWeek('')
            setFilteredFixtures([])
        })
    }

    function getFixtures(roundNumber = null) {
        setLoadingFixtures(true)
        const base = roundNumber != null ? apiUrl(`/api/v1/fixtures/${roundNumber}`) : apiUrl('/api/v1/fixtures')
        const url = base + (effectiveCompetition ? `?competition=${encodeURIComponent(effectiveCompetition)}` : '')
        fetch(url)
            .then(response => {
                if (!response.ok) throw new Error('Failed to fetch fixtures')
                return response.json()
            })
            .then(fixturesData => {
                const fixturesList = Array.isArray(fixturesData) ? fixturesData : []
                const sorted = [...fixturesList].sort((a, b) => {
                    const ad = a?.fixture_date ? new Date(a.fixture_date).getTime() : Number.POSITIVE_INFINITY
                    const bd = b?.fixture_date ? new Date(b.fixture_date).getTime() : Number.POSITIVE_INFINITY
                    return ad - bd
                })
                setFilteredFixtures(sorted)
            })
            .catch(error => {
                console.error('Error fetching fixtures:', error)
                setFilteredFixtures([])
            })
            .finally(() => setLoadingFixtures(false))
    }

    function refreshScores({ silent = false } = {}) {
        if (!leagueId && !effectiveCompetition) return
        const params = new URLSearchParams({
            competition: effectiveCompetition || 'eng.1',
            scores_only: 'true',
        })
        const pathWithQuery = '/api/v1/fixtures/sync-scores?' + params.toString()
        if (!silent) {
            setSyncing(true)
            setSyncMessage(null)
        }
        authenticatedFetch(pathWithQuery, { method: 'GET' })
            .then(res => {
                if (res.status === 404) {
                    const e = new Error('Refresh scores not available')
                    e.status = 404
                    throw e
                }
                return res.ok ? res.json() : res.json().then(err => { throw new Error(err.error || 'Sync failed') })
            })
            .then(data => {
                if (data && data.error) throw new Error(data.error)
                if (!silent) {
                    const updated = data.fixtures_updated ?? data.updated ?? 0
                    const notFound = data.fixtures_not_found ?? 0
                    setSyncMessage({
                        type: 'success',
                        text: `Scores refreshed: ${updated} fixture(s) updated.${notFound ? ` ${notFound} API match(es) had no matching fixture.` : ''}`
                    })
                }
                return authenticatedFetch('/api/v1/predictions/check-results', { method: 'POST' })
            })
            .then(() => {
                getPredictions()
                fetchLeaderboard()
                if (leagueId) getAvailableRounds()
                if (leagueId) loadCurrentRoundAndFixtures()
            })
            .catch(err => {
                if (silent) return
                const msg = err.status === 404
                    ? 'Refresh scores is not available on this server. Deploy the latest backend (with /api/v1/fixtures/sync-scores) to enable it.'
                    : (err.message || 'Failed to refresh scores.')
                setSyncMessage({ type: 'error', text: msg })
            })
            .finally(() => {
                if (!silent) {
                    setSyncing(false)
                    setTimeout(() => setSyncMessage(null), 6000)
                }
            })
    }

    function syncFixtures() {
        setSyncing(true)
        setSyncMessage(null)
        const comp = competitions.find(c => c.slug === effectiveCompetition)
        const isFootballData = comp && comp.source === 'football_data'
        const isEspn = comp && comp.source === 'espn'
        let body
        if (isFootballData) {
            body = { source: 'football_data', league_slug: comp.slug, competition: comp.slug }
        } else if (isEspn) {
            body = { source: 'espn', league_slug: comp.espn_slug || comp.slug }
        } else if (effectiveCompetition === 'eng.1' || (comp && comp.slug === 'eng.1')) {
            body = { source: 'football_data', league_slug: 'eng.1', competition: 'eng.1' }
        } else {
            body = { source: 'football_data', league_slug: effectiveCompetition || 'eng.1', competition: effectiveCompetition || 'eng.1' }
        }
        fetch(apiUrl('/api/v1/fixtures/sync'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        })
        .then(async response => {
            const contentType = response.headers.get('content-type')
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text()
                throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}`)
            }
            
            const data = await response.json()
            if (!response.ok) {
                throw new Error(data.error || `Server error: ${response.status}`)
            }
            return data
        })
        .then(async data => {
            if (data.error) {
                setSyncMessage({ type: 'error', text: data.error })
                return
            }
            const details = [
                data.added != null ? `added=${data.added}` : null,
                data.updated != null ? `updated=${data.updated}` : null,
                data.total_written != null ? `total=${data.total_written}` : null,
                data.window_start_utc && data.window_end_utc ? `window=${data.window_start_utc} → ${data.window_end_utc}` : null,
                data.fixtures_seen != null ? `seen=${data.fixtures_seen}` : null,
                data.fixtures_with_parsed_date != null ? `parsed_dates=${data.fixtures_with_parsed_date}` : null,
                data.fixtures_in_window != null ? `in_window=${data.fixtures_in_window}` : null,
                data.skipped_no_date != null ? `skipped_no_date=${data.skipped_no_date}` : null,
                data.skipped_outside_window != null ? `skipped_outside_window=${data.skipped_outside_window}` : null,
                data.parsed_min_date_utc ? `api_min=${data.parsed_min_date_utc}` : null,
                data.parsed_max_date_utc ? `api_max=${data.parsed_max_date_utc}` : null,
            ].filter(Boolean).join(' | ')

            let text = `Sync complete. ${details}`
            // After syncing fixture list, pull in latest scores for this league (all use competition=slug)
            try {
                const scoreParams = new URLSearchParams({
                    competition: effectiveCompetition || 'eng.1',
                    scores_only: 'true',
                })
                const scoreRes = await authenticatedFetch('/api/v1/fixtures/sync-scores?' + scoreParams.toString(), { method: 'GET' })
                const scoreData = await (scoreRes.ok ? scoreRes.json() : scoreRes.json().then(() => ({})))
                const scoreUpdated = scoreData.fixtures_updated ?? scoreData.updated ?? 0
                if (scoreUpdated > 0) text += ` Scores updated: ${scoreUpdated} fixture(s).`
                await authenticatedFetch('/api/v1/predictions/check-results', { method: 'POST' }).catch(() => {})
                getPredictions()
                fetchLeaderboard()
            } catch (_) { /* non-fatal */ }
            setSyncMessage({ type: 'success', text })
            getAvailableRounds()
            loadCurrentRoundAndFixtures()
        })
        .catch(error => {
            console.error('Error syncing fixtures:', error)
            setSyncMessage({ type: 'error', text: error.message || 'Failed to sync fixtures. Please check the backend console for details.' })
        })
        .finally(() => {
            setSyncing(false)
            setTimeout(() => setSyncMessage(null), 5000)
        })
    }

    function getPredictions() {
        setLoadingPredictions(true)
        const url = leagueId ? `/api/v1/predictions?league_id=${leagueId}` : '/api/v1/predictions'
        authenticatedFetch(url)
        .then(response => {
            if (!response.ok) {
                throw new Error('Failed to fetch predictions')
            }
            return response.json()
        })
        .then(data => {
            console.log('Predictions fetched:', data)
            console.log('Debug info:', data.debug)
            const preds = data.predictions || []
            console.log('Total predictions:', preds.length)
            const completed = preds.filter(p => p.fixture && p.fixture.is_completed && p.fixture.actual_home_score !== null && p.fixture.actual_away_score !== null)
            console.log('Completed predictions:', completed.length)
            setPredictions(preds)
        })
        .catch(error => {
            console.error('Error fetching predictions:', error)
            setPredictions([])
        })
        .finally(() => {
            setLoadingPredictions(false)
        })
    }

    function fetchLeaderboard(round = null) {
        if (!leagueId) return
        setLoadingLeaderboard(true)
        const roundQ = round != null ? `&round=${round}` : ''
        authenticatedFetch(`/api/v1/leagues/${leagueId}/leaderboard?t=${Date.now()}${roundQ}`)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch leaderboard')
                }
                return response.json()
            })
            .then(data => {
                setLeaderboard(data.leaderboard || [])
                setLeaderboardScope(data.scope || 'full_season')
                setLeaderboardCurrentRound(data.current_round ?? null)
            })
            .catch(error => {
                console.error('Error fetching leaderboard:', error)
                setLeaderboard([])
                setLeaderboardScope('full_season')
                setLeaderboardCurrentRound(null)
            })
            .finally(() => {
                setLoadingLeaderboard(false)
            })
    }

    function refreshLeagueDetail() {
        if (!leagueId) return
        fetchLeagueById(authenticatedFetch, leagueId)
            .then((league) => {
                setLeagueDetail(league || null)
                applyLeagueCompetitionToState(league, setSelectedCompetition)
            })
            .catch(() => setLeagueDetail(null))
        fetchLeaderboard()
    }

    const myMembership = leagueMembers.find(m => Number(m.id) === Number(currentUser?.id))

    function handleOpenDisplayNameDialog() {
        setDisplayNameDraft(myMembership?.display_name ?? '')
        setDisplayNameError(null)
        setDisplayNameDialogOpen(true)
    }

    function handleSaveDisplayName() {
        const name = (displayNameDraft || '').trim()
        if (!name) {
            setDisplayNameError('Display name cannot be empty')
            return
        }
        if (!leagueId) return
        setDisplayNameError(null)
        setDisplayNameSaving(true)
        authenticatedFetch(`/api/v1/leagues/${leagueId}/me`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ display_name: name }),
        })
            .then(res => res.json().then(data => ({ ok: res.ok, data })))
            .then(({ ok, data }) => {
                if (ok) {
                    setDisplayNameDialogOpen(false)
                    refreshLeagueDetail()
                } else {
                    setDisplayNameError(data.error || 'Failed to update display name')
                }
            })
            .catch(() => setDisplayNameError('Failed to update display name. Please try again.'))
            .finally(() => setDisplayNameSaving(false))
    }

    function handleStartNewSeason() {
        if (!leagueId) return
        setStartingNewSeason(true)
        setSyncMessage(null)
        authenticatedFetch(`/api/v1/leagues/${leagueId}/start-new-season`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sync_fixtures: newSeasonSyncFixtures }),
        })
            .then(res => res.json().then(data => ({ ok: res.ok, data })))
            .then(({ ok, data }) => {
                if (!ok) throw new Error(data.error || 'Failed to start new season')
                const sync = data.fixture_sync
                let text = data.message || 'New season started.'
                if (sync?.error) {
                    text += ` Fixture sync failed: ${sync.error}`
                } else if (sync?.total_written != null) {
                    text += ` Fixtures synced (added=${sync.added ?? 0}, updated=${sync.updated ?? 0}).`
                }
                setSyncMessage({ type: sync?.error ? 'warning' : 'success', text })
                setNewSeasonDialogOpen(false)
                fetchLeagueById(authenticatedFetch, leagueId).then((league) => {
                    if (league) setLeagueDetail(league)
                })
                fetchLeaderboard()
                getAvailableRounds()
                loadCurrentRoundAndFixtures()
                getPredictions()
            })
            .catch(err => {
                setSyncMessage({ type: 'error', text: err.message || 'Failed to start new season' })
            })
            .finally(() => {
                setStartingNewSeason(false)
                setTimeout(() => setSyncMessage(null), 8000)
            })
    }

    async function handleDeleteLeague() {
        if (!leagueId || deletingLeague) return
        const idToDelete = leagueId
        setDeletingLeague(true)
        setDeleteLeagueError(null)
        // Cancel long-running sync/check-results so DELETE is not stuck behind them (single-worker dev server).
        leagueOpsAbortRef.current?.abort()
        leagueOpsAbortRef.current = null
        setSyncing(false)
        setDeleteLeagueDialog(false)
        setLeagueId(null)
        setLeagueDetail(null)
        history.replace('/leagues')
        const timeoutMs = 90000
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
        try {
            const res = await authenticatedFetch(`/api/v1/leagues/${idToDelete}`, {
                method: 'DELETE',
                signal: controller.signal,
            })
            let data = null
            const body = await res.text()
            if (body) {
                try {
                    data = JSON.parse(body)
                } catch (_) {
                    /* non-JSON error body */
                }
            }
            if (!res.ok) {
                throw new Error(data?.error || `Failed to delete league (${res.status})`)
            }
            removeLeagueFromSnapshot(idToDelete)
            history.replace({ pathname: '/leagues', state: { refreshLeagues: true } })
        } catch (err) {
            const message = err.name === 'AbortError'
                ? 'Delete timed out. Check your leagues list—the league may or may not have been removed.'
                : (err.message || 'Failed to delete league')
            history.replace({ pathname: '/leagues', state: { flash: { type: 'error', text: message } } })
        } finally {
            clearTimeout(timeoutId)
            setDeletingLeague(false)
        }
    }

    function handleRemoveMember(memberUserId) {
        if (!leagueId) return
        setRemovingMemberId(memberUserId)
        authenticatedFetch(`/api/v1/leagues/${leagueId}/members/${memberUserId}`, { method: 'DELETE' })
            .then(res => {
                if (res.ok) {
                    refreshLeagueDetail()
                } else {
                    return res.json().then(data => { throw new Error(data.error || 'Failed to remove member') })
                }
            })
            .catch(err => {
                setSyncMessage({ type: 'error', text: err.message || 'Failed to remove member' })
                setTimeout(() => setSyncMessage(null), 5000)
            })
            .finally(() => setRemovingMemberId(null))
    }

    function handleUpdateMemberRole(memberUserId, newRole) {
        if (!leagueId || !newRole) return
        setUpdatingRoleId(memberUserId)
        authenticatedFetch(`/api/v1/leagues/${leagueId}/members/${memberUserId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ role: newRole })
        })
            .then(res => {
                if (res.ok) {
                    refreshLeagueDetail()
                } else {
                    return res.json().then(data => { throw new Error(data.error || 'Failed to update role') })
                }
            })
            .catch(err => {
                setSyncMessage({ type: 'error', text: err.message || 'Failed to update role' })
                setTimeout(() => setSyncMessage(null), 5000)
            })
            .finally(() => setUpdatingRoleId(null))
    }

    function handleFetchMemberPredictions(memberUserId) {
        if (!leagueId || !memberUserId) return
        setLoadingMemberPredictions(true)
        setAdminMemberPredictions([])
        authenticatedFetch(`/api/v1/leagues/${leagueId}/members/${memberUserId}/predictions`)
            .then(res => {
                if (!res.ok) throw new Error('Failed to load predictions')
                return res.json()
            })
            .then(data => {
                setAdminMemberPredictions(data.predictions || [])
            })
            .catch(() => setAdminMemberPredictions([]))
            .finally(() => setLoadingMemberPredictions(false))
    }

    function handleAdminUpdatePrediction(gameId, home_team_score, away_team_score) {
        if (!leagueId || gameId == null) return
        authenticatedFetch(`/api/v1/leagues/${leagueId}/games/${gameId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ home_team_score: Number(home_team_score), away_team_score: Number(away_team_score) })
        })
            .then(res => {
                if (!res.ok) return res.json().then(data => { throw new Error(data.error || 'Failed to update') })
                setEditPredictionDialog(null)
                handleFetchMemberPredictions(adminSelectedMemberId)
                fetchLeaderboard()
            })
            .catch(err => {
                setSyncMessage({ type: 'error', text: err.message || 'Failed to update prediction' })
                setTimeout(() => setSyncMessage(null), 5000)
            })
    }

    function handleAdminCreatePrediction(memberUserId, home_team, away_team, home_team_score, away_team_score) {
        if (!leagueId || !memberUserId) return
        authenticatedFetch(`/api/v1/leagues/${leagueId}/members/${memberUserId}/predictions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ home_team, away_team, home_team_score: Number(home_team_score), away_team_score: Number(away_team_score) })
        })
            .then(res => {
                if (!res.ok) return res.json().then(data => { throw new Error(data.error || 'Failed to add prediction') })
                setAddPredictionDialog(null)
                handleFetchMemberPredictions(adminSelectedMemberId)
                if (adminPredictionRound) getAdminFixturesForRound(Number(adminPredictionRound))
                fetchLeaderboard()
            })
            .catch(err => {
                setSyncMessage({ type: 'error', text: err.message || 'Failed to add prediction' })
                setTimeout(() => setSyncMessage(null), 5000)
            })
    }

    function handleAdminSetFixtureRound() {
        if (!leagueId || !roundOverrideDialog?.fixtureId) return
        const parsedRound = parseInt(roundOverrideDialog.new_round, 10)
        if (Number.isNaN(parsedRound) || parsedRound <= 0) {
            setSyncMessage({ type: 'error', text: `${isWorldCup ? 'Day' : 'Week'} must be a positive number.` })
            setTimeout(() => setSyncMessage(null), 5000)
            return
        }
        setSavingRoundOverride(true)
        authenticatedFetch(`/api/v1/leagues/${leagueId}/fixtures/${roundOverrideDialog.fixtureId}/round`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                fixture_round: parsedRound,
                manual_round_override: !!roundOverrideDialog.manual_round_override,
            }),
        })
            .then(res => {
                if (!res.ok) return res.json().then(data => { throw new Error(data.error || 'Failed to update fixture week') })
                return res.json()
            })
            .then(data => {
                setRoundOverrideDialog(null)
                const oldRound = data?.old_round
                setSyncMessage({
                    type: 'success',
                    text: `${roundOverrideDialog.home_team} vs ${roundOverrideDialog.away_team} moved from ${weekOrDayTitle(oldRound || '?')} to ${weekOrDayTitle(parsedRound)}. ${data?.affected_games ?? 0} saved prediction row(s) updated.`,
                })
                setTimeout(() => setSyncMessage(null), 6000)
                getAvailableRounds()
                if (adminPredictionRound) getAdminFixturesForRound(Number(adminPredictionRound))
                if (gameWeek) getFixtures(Number(gameWeek))
                getPredictions()
                fetchLeaderboard()
            })
            .catch(err => {
                setSyncMessage({ type: 'error', text: err.message || 'Failed to update fixture week.' })
                setTimeout(() => setSyncMessage(null), 5000)
            })
            .finally(() => setSavingRoundOverride(false))
    }

    function getAdminFixturesForRound(roundNum) {
        if (roundNum == null || roundNum === '') {
            setAdminFixturesForRound([])
            return
        }
        setLoadingAdminFixtures(true)
        const url = apiUrl(`/api/v1/fixtures/${roundNum}`) + (effectiveCompetition ? `?competition=${encodeURIComponent(effectiveCompetition)}` : '')
        const norm = (s) => (String(s || '').toLowerCase().trim()
            .replace(/&/g, ' and ')
            .replace(/^afc\s+/, '').replace(/^fc\s+/, '')
            .replace(/\s+fc\s*$/i, '').replace(/\s+afc\s*$/i, '')
            .replace(/\s+/g, ' ')
        )
        fetch(url)
            .then(res => res.ok ? res.json() : [])
            .then(data => {
                const list = Array.isArray(data) ? data : []
                const seen = new Set()
                const deduped = list.filter(f => {
                    const home = f.fixture_home_team || f.home_team || ''
                    const away = f.fixture_away_team || f.away_team || ''
                    const key = `${norm(home)}|${norm(away)}`
                    if (seen.has(key)) return false
                    seen.add(key)
                    return true
                })
                setAdminFixturesForRound([...deduped].sort((a, b) => {
                    const ad = a?.fixture_date ? new Date(a.fixture_date).getTime() : 0
                    const bd = b?.fixture_date ? new Date(b.fixture_date).getTime() : 0
                    return ad - bd
                }))
            })
            .catch(() => setAdminFixturesForRound([]))
            .finally(() => setLoadingAdminFixtures(false))
    }

    // Load rounds/predictions on mount only when not opening a specific league (league effect handles ?league=)
    useEffect(() => {
        const hasLeagueInUrl = /\bleague=\d+/.test(location.search)
        if (hasLeagueInUrl) return
        getAvailableRounds()
        getPredictions()
        loadCurrentRoundAndFixtures()
    }, [])

    // When league is selected, refresh default game week and predictions (with league_id so fixture resolution uses correct competition)
    useEffect(() => {
        if (!leagueId || leagueDetail?.id !== leagueId || !effectiveCompetition) return
        loadCurrentRoundAndFixtures()
        getAvailableRounds()
        getPredictions()
    }, [leagueId, leagueDetail, effectiveCompetition])

    // When competition changes (non-league picker or after league loads), refresh rounds and fixtures
    useEffect(() => {
        if (leagueId && !effectiveCompetition) return
        getAvailableRounds()
        loadCurrentRoundAndFixtures()
    }, [selectedCompetition, leagueId, effectiveCompetition])

    // When user selects a different week from dropdown (weekly leagues), refetch leaderboard for that week
    useEffect(() => {
        if (leagueId && leaderboardScope === 'weekly' && gameWeek) {
            const roundNum = parseInt(gameWeek, 10)
            if (!Number.isNaN(roundNum)) fetchLeaderboard(roundNum)
        }
    }, [gameWeek, leaderboardScope, leagueId])

    const handleDropdownChange = (e) => {
        const selectedValue = e.target.value;
        setGameWeek(selectedValue);
        if (selectedValue === '' || selectedValue === 'all') {
            setFilteredFixtures([])
        } else {
            getFixtures(parseInt(selectedValue))
        }
    }


    return (
        <>
        <Navbar />
        <Box component="div" sx={{ px: 2, pr: { xs: 2, sm: 3, md: 4 }, pt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button 
                    variant="outlined" 
                    color="primary"
                    onClick={() => history.push('/leagues')}
                >
                    ← Back to Leagues
                </Button>
                {isAdmin && (
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<AdminPanelSettingsIcon />}
                        onClick={() => setAdminPanelOpen(true)}
                    >
                        Admin
                    </Button>
                )}
            </Box>
        </Box>
        
        {syncMessage && (
            <Alert severity={syncMessage.type} sx={{ mb: 2 }}>
                {syncMessage.text}
            </Alert>
        )}
        
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" component="label">League:</Typography>
                {leagueDetail?.competition_slug ? (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {competitions.find(c => c.slug === leagueDetail.competition_slug)?.name || leagueDetail.competition_slug}
                    </Typography>
                ) : (
                    <select
                        value={selectedCompetition}
                        onChange={(e) => setSelectedCompetition(e.target.value)}
                        style={{ padding: '8px', fontSize: '16px', minWidth: '180px' }}
                    >
                        {competitions.length ? competitions.map(c => (
                            <option key={c.slug} value={c.slug}>{c.name}</option>
                        )) : (
                            <option value="eng.1">English Premier League</option>
                        )}
                    </select>
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" component="label">{weekOrDayLabel}:</Typography>
                <select
                    value={gameWeek}
                    onChange={handleDropdownChange}
                    style={{ padding: '8px', fontSize: '16px', minWidth: '150px' }}
                >
                    <option value="">-- Select {isWorldCup ? 'Day' : 'Week'} --</option>
                    {availableRounds.map(round => (
                        <option key={round} value={round}>{weekOrDayTitle(round)}</option>
                    ))}
                </select>
            </Box>
        </Box>

        {!leagueId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
                Missing league selection. Please go back to Leagues and pick a league.
                <Box sx={{ mt: 1 }}>
                    <Button variant="outlined" color="primary" size="small" onClick={() => history.push('/leagues')}>
                        Go to Leagues
                    </Button>
                </Box>
            </Alert>
        )}

        <Grid container spacing={3}>
            {/* Left side: Predictions and Results - pl: 2 on md so table left edge aligns with PL table in right column */}
            <Grid item xs={12} md={7} sx={{ pl: { md: 2 } }}>
                {gameWeek ? (
                    <>
                        <Typography variant="h5" component="h2" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
                            {weekOrDayTitle(gameWeek)}
                        </Typography>

                        {loadingFixtures ? (
                            <Typography variant="body1">Loading fixtures...</Typography>
                        ) : filteredFixtures.length > 0 ? (
                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                <Table size="small" aria-label="predictions">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: 'primary.light' }}>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Home</TableCell>
                                            <TableCell align="center" sx={{ width: 90, fontWeight: 700, fontSize: '1rem' }}>Score</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Away</TableCell>
                                            <TableCell align="center" sx={{ width: 90, fontWeight: 700, fontSize: '1rem' }}>Score</TableCell>
                                            <TableCell align="center" sx={{ width: 80, fontWeight: 700, fontSize: '1rem' }}>Actual</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Day/Time</TableCell>
                                            <TableCell align="center" sx={{ width: 80, fontWeight: 700, fontSize: '1rem' }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredFixtures.map((fixture) => {
                                            const norm = (s) => (String(s || '').toLowerCase().trim()
                                                .replace(/&/g, ' and ')
                                                .replace(/^afc\s+/, '').replace(/^fc\s+/, '')
                                                .replace(/\s+fc\s*$/i, '').replace(/\s+afc\s*$/i, '')
                                                .replace(/\s+/g, ' ')
                                            )
                                            const matchingPrediction = predictions.find(p =>
                                                p.fixture && p.fixture.id && fixture.id && Number(p.fixture.id) === Number(fixture.id)
                                            ) || predictions.find(p =>
                                                p.home_team && p.away_team &&
                                                fixture.fixture_home_team != null && fixture.fixture_away_team != null &&
                                                norm(p.home_team) === norm(fixture.fixture_home_team) &&
                                                norm(p.away_team) === norm(fixture.fixture_away_team)
                                            )
                                            return (
                                                <Predictions
                                                    key={fixture.id}
                                                    fixture={fixture}
                                                    existingPrediction={matchingPrediction}
                                                    onPredictionSaved={() => {
                                                        getPredictions()
                                                        if (leagueId) fetchLeaderboard()
                                                    }}
                                                    asTableRow
                                                    allowAskAi={leagueDetail?.ai_predictions_enabled === true}
                                                    leagueId={leagueId}
                                                />
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                No fixtures found for {isWorldCup ? 'day' : 'week'} {gameWeek}. Please sync fixtures first.
                            </Typography>
                        )}

                        {/* Results table - below predictions on desktop */}
                        {gameWeek && (
                            <Box sx={{ mt: 3, display: { xs: 'none', md: 'block' } }}>
                                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                                    Results - {weekOrDayTitle(gameWeek)}
                                </Typography>
                                {loadingPredictions ? (
                                    <Typography variant="body2">Loading results...</Typography>
                                ) : (() => {
                                    const selectedRound = parseInt(gameWeek, 10)
                                    const norm = (s) => (String(s || '').toLowerCase().trim()
                                        .replace(/&/g, ' and ')
                                        .replace(/^afc\s+/, '').replace(/^fc\s+/, '')
                                        .replace(/\s+fc\s*$/i, '').replace(/\s+afc\s*$/i, '')
                                        .replace(/\s+/g, ' ')
                                    )
                                    // Build from completed fixtures in this round; dedupe by match (same normalized home/away) so duplicate DB rows don't show twice
                                    const allCompletedForRound = (filteredFixtures || []).filter(
                                        f => f.fixture_round != null && Number(f.fixture_round) === selectedRound &&
                                        f.actual_home_score != null && f.actual_away_score != null &&
                                        fixtureHasStarted(f)
                                    )
                                    const seenMatch = new Set()
                                    const completedFixturesForRound = allCompletedForRound.filter(f => {
                                        const key = `${norm(f.fixture_home_team)}|${norm(f.fixture_away_team)}`
                                        if (seenMatch.has(key)) return false
                                        seenMatch.add(key)
                                        return true
                                    })
                                    const completedPredictions = completedFixturesForRound
                                        .map(fixture => {
                                            const pred = predictions.find(p =>
                                                p.fixture?.id && Number(p.fixture.id) === Number(fixture.id)
                                            ) || predictions.find(p =>
                                                p.home_team && p.away_team &&
                                                norm(p.home_team) === norm(fixture.fixture_home_team) &&
                                                norm(p.away_team) === norm(fixture.fixture_away_team)
                                            )
                                            if (!pred) return null
                                            return { ...pred, fixture: { ...(pred.fixture || {}), ...fixture, actual_home_score: fixture.actual_home_score, actual_away_score: fixture.actual_away_score, round: fixture.fixture_round, date: fixture.fixture_date } }
                                        })
                                        .filter(Boolean)
                                        .sort((a, b) => {
                                            const dateA = a.fixture?.date ? new Date(a.fixture.date).getTime() : 0
                                            const dateB = b.fixture?.date ? new Date(b.fixture.date).getTime() : 0
                                            return dateA - dateB
                                        })
                                    if (completedPredictions.length === 0) {
                                        return (
                                            <Typography variant="body2" sx={{ mt: 2 }}>
                                                No completed predictions for this {isWorldCup ? 'day' : 'week'} yet.
                                            </Typography>
                                        )
                                    }
                                    const getDisplayResult = (p) => {
                                        const f = p.fixture
                                        if (!fixtureHasStarted(f)) return null
                                        if (p.game_result) return p.game_result
                                        if (f?.actual_home_score == null || f?.actual_away_score == null || p.home_team_score == null || p.away_team_score == null) return null
                                        const predSame = Number(p.home_team_score) === Number(f.actual_home_score) && Number(p.away_team_score) === Number(f.actual_away_score)
                                        if (predSame) return 'Win'
                                        const ph = Number(p.home_team_score), pa = Number(p.away_team_score), ah = Number(f.actual_home_score), aa = Number(f.actual_away_score)
                                        const predW = ph > pa ? 'home' : (pa > ph ? 'away' : 'draw')
                                        const actW = ah > aa ? 'home' : (aa > ah ? 'away' : 'draw')
                                        return predW === actW ? 'Draw' : 'Loss'
                                    }
                                    const wins = completedPredictions.filter(p => getDisplayResult(p) === 'Win').length
                                    const draws = completedPredictions.filter(p => getDisplayResult(p) === 'Draw').length
                                    const losses = completedPredictions.filter(p => getDisplayResult(p) === 'Loss').length
                                    return (
                                        <>
                                            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip label={`W: ${wins}`} color="success" size="small" />
                                                <Chip label={`D: ${draws}`} color="warning" size="small" />
                                                <Chip label={`L: ${losses}`} color="error" size="small" />
                                            </Box>
                                            <Box>
                                                {completedPredictions.map((prediction) => {
                                                    const { fixture, home_team, away_team, home_team_score, away_team_score } = prediction
                                                    const displayResult = getDisplayResult(prediction)
                                                    let resultColor = 'default'
                                                    if (displayResult === 'Win') resultColor = 'success'
                                                    else if (displayResult === 'Draw') resultColor = 'warning'
                                                    else if (displayResult === 'Loss') resultColor = 'error'
                                                    return (
                                                        <Card key={prediction.id} sx={{ mb: 1.5 }}>
                                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                    <Typography variant="subtitle2">{fixture?.round != null ? weekOrDayTitle(fixture.round) : 'N/A'}</Typography>
                                                                    <Chip label={displayResult || 'Pending'} color={resultColor} size="small" />
                                                                </Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                    {home_team} vs {away_team}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                    <Typography variant="caption">Pred: {home_team_score}-{away_team_score}</Typography>
                                                                    <Typography variant="caption" color="textSecondary">Actual: {fixture.actual_home_score}-{fixture.actual_away_score}</Typography>
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    )
                                                })}
                                            </Box>
                                        </>
                                    )
                                })()}
                            </Box>
                        )}
                    </>
                ) : (
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Please select a {isWorldCup ? 'day' : 'game week'} to view and make predictions for fixtures.
                    </Typography>
                )}
            </Grid>

            {/* Right side: League table (all leagues except World Cup) + Leaderboard */}
            <Grid item xs={12} md={5} sx={{ pl: { md: 2 }, pr: { xs: 2, md: 4 } }}>
                {effectiveCompetition && effectiveCompetition !== 'fifa.world' && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ px: 0, '&:last-child': { pb: 2 }, pt: 2, pb: 0 }}>
                        <Typography variant="h6" component="h3" sx={{ mb: 1.5, fontWeight: 600, px: 2 }}>
                            {standingsTitle || 'League table'}
                        </Typography>
                        {loadingStandings ? (
                            <Typography variant="body2" color="text.secondary">Loading…</Typography>
                        ) : standingsError ? (
                            <Typography variant="body2" color="error" sx={{ px: 2 }}>{standingsError}</Typography>
                        ) : plStandings && plStandings.length > 0 ? (
                            <TableContainer sx={{ maxHeight: 340, overflow: 'auto', px: 0 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                            <TableCell sx={{ fontWeight: 700, width: 32 }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, width: 36 }}>P</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, width: 36 }}>W</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, width: 36 }}>D</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, width: 36 }}>L</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, width: 36 }}>GF</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, width: 36 }}>GA</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, width: 40 }}>GD</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, width: 40 }}>Pts</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {plStandings.map((row) => (
                                            <TableRow key={row.position}>
                                                <TableCell sx={{ fontWeight: 500 }}>{row.position}</TableCell>
                                                <TableCell>{row.team}</TableCell>
                                                <TableCell align="center">{row.played}</TableCell>
                                                <TableCell align="center">{row.won}</TableCell>
                                                <TableCell align="center">{row.drawn}</TableCell>
                                                <TableCell align="center">{row.lost}</TableCell>
                                                <TableCell align="center">{row.goalsFor}</TableCell>
                                                <TableCell align="center">{row.goalsAgainst}</TableCell>
                                                <TableCell align="center">{row.goalDifference >= 0 ? `+${row.goalDifference}` : row.goalDifference}</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600 }}>{row.points}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>No standings data.</Typography>
                        )}
                    </CardContent>
                </Card>
                )}
                <Box sx={{ position: 'sticky', top: 20 }}>
                    {/* Your display name in this league */}
                    {leagueId && myMembership && (
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">
                                Your display name in this league:
                            </Typography>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                                {myMembership.display_name}
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={handleOpenDisplayNameDialog}
                            >
                                Update name
                            </Button>
                        </Box>
                    )}
                    {/* Leaderboard Table */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
                            League Leaderboard
                            {leaderboardScope === 'weekly' && leaderboardCurrentRound != null && (
                                <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1, fontWeight: 'normal' }}>
                                    ({weekOrDayTitle(leaderboardCurrentRound)})
                                </Typography>
                            )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {leaderboardScope === 'weekly'
                                ? (isWorldCup ? 'Points this day. "Days won" = number of days you placed first (including ties).' : 'Points this week. "Weeks won" = number of weeks you placed first (including ties).')
                                : (isWorldCup ? 'All days' : 'All game weeks (1-38)')}
                        </Typography>
                        {loadingLeaderboard ? (
                            <Typography variant="body2">Loading leaderboard...</Typography>
                        ) : (() => {
                            const displayLeaderboard = leaderboard.length > 0
                                ? leaderboard
                                : (leagueMembers.length > 0
                                    ? leagueMembers.map(m => ({
                                        user_id: m.id,
                                        display_name: m.display_name ?? m.email ?? 'Member',
                                        wins: 0,
                                        draws: 0,
                                        losses: 0,
                                        points: 0,
                                        weeks_won: 0,
                                    }))
                                    : [])
                            return displayLeaderboard.length > 0 ? (
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Rank</strong></TableCell>
                                                <TableCell><strong>Player</strong></TableCell>
                                                <TableCell align="right"><strong>Points</strong></TableCell>
                                                <TableCell align="right"><strong>W</strong></TableCell>
                                                <TableCell align="right"><strong>D</strong></TableCell>
                                                <TableCell align="right"><strong>L</strong></TableCell>
                                                {leaderboardScope === 'weekly' && (
                                                    <TableCell align="right"><strong>{isWorldCup ? 'Days won' : 'Weeks won'}</strong></TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {displayLeaderboard.map((player, index) => (
                                                <TableRow 
                                                    key={player.user_id}
                                                    sx={{ 
                                                        '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                                                        backgroundColor: index === 0 ? 'success.light' : 'inherit'
                                                    }}
                                                >
                                                    <TableCell><strong>#{index + 1}</strong></TableCell>
                                                    <TableCell>{player.display_name}</TableCell>
                                                    <TableCell align="right"><strong>{player.points}</strong></TableCell>
                                                    <TableCell align="right">{player.wins}</TableCell>
                                                    <TableCell align="right">{player.draws}</TableCell>
                                                    <TableCell align="right">{player.losses}</TableCell>
                                                    {leaderboardScope === 'weekly' && (
                                                        <TableCell align="right">{player.weeks_won ?? 0}</TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No leaderboard data available yet.
                                </Typography>
                            )
                        })()}
                    </Box>

                    {/* League members (admin: remove) */}
                    {isAdmin && leagueMembers.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>League members</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Name</strong></TableCell>
                                            <TableCell><strong>Role</strong></TableCell>
                                            <TableCell align="right"><strong>Actions</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {leagueMembers.map((m) => (
                                            <TableRow key={m.id}>
                                                <TableCell>{m.display_name}</TableCell>
                                                <TableCell>
                                                    <FormControl size="small" sx={{ minWidth: 100 }} disabled={updatingRoleId === m.id}>
                                                        <Select
                                                            value={m.role === 'admin' ? 'admin' : 'player'}
                                                            onChange={(e) => handleUpdateMemberRole(m.id, e.target.value)}
                                                            sx={{ height: 28, fontSize: '0.875rem' }}
                                                        >
                                                            <MenuItem value="player">Player</MenuItem>
                                                            <MenuItem value="admin">Admin</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {Number(m.id) !== Number(currentUser?.id) && (
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                            disabled={removingMemberId === m.id}
                                                            onClick={() => window.confirm(`Remove ${m.display_name} from the league?`) && handleRemoveMember(m.id)}
                                                        >
                                                            {removingMemberId === m.id ? 'Removing...' : 'Remove'}
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Results Section - mobile only (on desktop shown below predictions in left column) */}
                    {gameWeek && (
                        <Box sx={{ mt: 3, display: { xs: 'block', md: 'none' } }}>
                            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                                Results - {weekOrDayTitle(gameWeek)}
                            </Typography>

                            {loadingPredictions ? (
                                <Typography variant="body2">Loading results...</Typography>
                            ) : (() => {
                                const selectedRound = parseInt(gameWeek, 10)
                                const norm = (s) => (String(s || '').toLowerCase().trim()
                                    .replace(/&/g, ' and ')
                                    .replace(/^afc\s+/, '').replace(/^fc\s+/, '')
                                    .replace(/\s+fc\s*$/i, '').replace(/\s+afc\s*$/i, '')
                                    .replace(/\s+/g, ' ')
                                )
                                const allCompletedForRoundMobile = (filteredFixtures || []).filter(
                                    f => f.fixture_round != null && Number(f.fixture_round) === selectedRound &&
                                    f.actual_home_score != null && f.actual_away_score != null &&
                                    fixtureHasStarted(f)
                                )
                                const seenMatchMobile = new Set()
                                const completedFixturesForRound = allCompletedForRoundMobile.filter(f => {
                                    const key = `${norm(f.fixture_home_team)}|${norm(f.fixture_away_team)}`
                                    if (seenMatchMobile.has(key)) return false
                                    seenMatchMobile.add(key)
                                    return true
                                })
                                const completedPredictions = completedFixturesForRound
                                    .map(fixture => {
                                        const pred = predictions.find(p =>
                                            p.fixture?.id && Number(p.fixture.id) === Number(fixture.id)
                                        ) || predictions.find(p =>
                                            p.home_team && p.away_team &&
                                            norm(p.home_team) === norm(fixture.fixture_home_team) &&
                                            norm(p.away_team) === norm(fixture.fixture_away_team)
                                        )
                                        if (!pred) return null
                                        return { ...pred, fixture: { ...(pred.fixture || {}), ...fixture, actual_home_score: fixture.actual_home_score, actual_away_score: fixture.actual_away_score, round: fixture.fixture_round, date: fixture.fixture_date } }
                                    })
                                    .filter(Boolean)
                                    .sort((a, b) => {
                                        const dateA = a.fixture?.date ? new Date(a.fixture.date).getTime() : 0
                                        const dateB = b.fixture?.date ? new Date(b.fixture.date).getTime() : 0
                                        return dateA - dateB
                                    })

                                if (completedPredictions.length === 0) {
                                    return (
                                        <Typography variant="body2" sx={{ mt: 2 }}>
                                            No completed predictions for this {isWorldCup ? 'day' : 'week'} yet.
                                        </Typography>
                                    )
                                }

                                const getDisplayResult = (p) => {
                                    const f = p.fixture
                                    if (!fixtureHasStarted(f)) return null
                                    if (p.game_result) return p.game_result
                                    if (f?.actual_home_score == null || f?.actual_away_score == null || p.home_team_score == null || p.away_team_score == null) return null
                                    const predSame = Number(p.home_team_score) === Number(f.actual_home_score) && Number(p.away_team_score) === Number(f.actual_away_score)
                                    if (predSame) return 'Win'
                                    const ph = Number(p.home_team_score), pa = Number(p.away_team_score), ah = Number(f.actual_home_score), aa = Number(f.actual_away_score)
                                    const predW = ph > pa ? 'home' : (pa > ph ? 'away' : 'draw')
                                    const actW = ah > aa ? 'home' : (aa > ah ? 'away' : 'draw')
                                    return predW === actW ? 'Draw' : 'Loss'
                                }
                                const wins = completedPredictions.filter(p => getDisplayResult(p) === 'Win').length
                                const draws = completedPredictions.filter(p => getDisplayResult(p) === 'Draw').length
                                const losses = completedPredictions.filter(p => getDisplayResult(p) === 'Loss').length

                                return (
                                    <>
                                        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip label={`W: ${wins}`} color="success" size="small" />
                                            <Chip label={`D: ${draws}`} color="warning" size="small" />
                                            <Chip label={`L: ${losses}`} color="error" size="small" />
                                        </Box>
                                        <Box>
                                            {completedPredictions.map((prediction) => {
                                                const { fixture, home_team, away_team, home_team_score, away_team_score } = prediction
                                                const displayResult = getDisplayResult(prediction)
                                                let resultColor = 'default'
                                                if (displayResult === 'Win') resultColor = 'success'
                                                else if (displayResult === 'Draw') resultColor = 'warning'
                                                else if (displayResult === 'Loss') resultColor = 'error'
                                                return (
                                                    <Card key={prediction.id} sx={{ mb: 1.5 }}>
                                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                <Typography variant="subtitle2">{fixture?.round != null ? weekOrDayTitle(fixture.round) : 'N/A'}</Typography>
                                                                <Chip label={displayResult || 'Pending'} color={resultColor} size="small" />
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                {home_team} vs {away_team}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography variant="caption">Pred: {home_team_score}-{away_team_score}</Typography>
                                                                <Typography variant="caption" color="textSecondary">Actual: {fixture.actual_home_score}-{fixture.actual_away_score}</Typography>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                )
                                            })}
                                        </Box>
                                    </>
                                )
                            })()}
                        </Box>
                    )}
                </Box>
            </Grid>
        </Grid>

        {isAdmin && (
            <AdminPanel
                open={adminPanelOpen}
                onClose={() => setAdminPanelOpen(false)}
                onSyncFixtures={syncFixtures}
                onRefreshScores={() => refreshScores()}
                onBackfill={() => setBackfillDialogOpen(true)}
                onStartNewSeason={() => setNewSeasonDialogOpen(true)}
                onDeleteLeague={() => {
                    setDeleteLeagueError(null)
                    setDeleteLeagueDialog(true)
                }}
                syncing={syncing}
                startingNewSeason={startingNewSeason}
            >
                {leagueMembers.length > 0 && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Edit predictions &amp; fixture {isWorldCup ? 'days' : 'weeks'}</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Select member</InputLabel>
                                <Select
                                    value={adminSelectedMemberId}
                                    label="Select member"
                                    onChange={(e) => setAdminSelectedMemberId(e.target.value)}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    {leagueMembers.map((m) => (
                                        <MenuItem key={m.id} value={String(m.id)}>{m.display_name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" fullWidth>
                                <InputLabel>{weekOrDayLabel}</InputLabel>
                                <Select
                                    value={adminPredictionRound || ''}
                                    label={weekOrDayLabel}
                                    onChange={(e) => {
                                        const r = e.target.value
                                        setAdminPredictionRound(r)
                                        if (!r) setAdminFixturesForRound([])
                                    }}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    {availableRounds.map((r) => (
                                        <MenuItem key={r} value={String(r)}>{weekOrDayTitle(r)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {loadingMemberPredictions ? (
                            <Typography variant="body2">Loading predictions...</Typography>
                        ) : adminSelectedMemberId && adminPredictionRound && adminFixturesForRound.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Match</TableCell>
                                            <TableCell align="center">Pred</TableCell>
                                            <TableCell align="center">Actual</TableCell>
                                            <TableCell align="right">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {adminFixturesForRound.map((fix) => {
                                            const home = fix.fixture_home_team || fix.home_team || ''
                                            const away = fix.fixture_away_team || fix.away_team || ''
                                            const norm = (s) => (String(s || '').toLowerCase().trim()
                                                .replace(/&/g, ' and ')
                                                .replace(/^afc\s+/, '').replace(/^fc\s+/, '')
                                                .replace(/\s+fc\s*$/i, '').replace(/\s+afc\s*$/i, '')
                                                .replace(/\s+/g, ' ')
                                            )
                                            const pred = adminMemberPredictions.find(p => p?.fixture?.id && fix?.id && Number(p.fixture.id) === Number(fix.id))
                                                || adminMemberPredictions.find(p => p?.home_team != null && p?.away_team != null && norm(p.home_team) === norm(home) && norm(p.away_team) === norm(away))
                                            const actualFromPred = pred?.fixture && pred.fixture.actual_home_score != null && pred.fixture.actual_away_score != null
                                                ? `${pred.fixture.actual_home_score}–${pred.fixture.actual_away_score}`
                                                : (fix.actual_home_score != null && fix.actual_away_score != null ? `${fix.actual_home_score}–${fix.actual_away_score}` : '–')
                                            return (
                                                <TableRow key={fix.id || `${home}-${away}`}>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{home} vs {away}</TableCell>
                                                    <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                                                        {pred ? `${pred.home_team_score ?? '–'}–${pred.away_team_score ?? '–'}` : <Typography variant="body2" color="text.secondary">No pick</Typography>}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                                        {actualFromPred}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                onClick={() => setRoundOverrideDialog({
                                                                    fixtureId: fix.id,
                                                                    home_team: home,
                                                                    away_team: away,
                                                                    current_round: fix.fixture_round,
                                                                    new_round: String(fix.fixture_round ?? ''),
                                                                    manual_round_override: true,
                                                                })}
                                                            >
                                                                Set {isWorldCup ? 'day' : 'week'}
                                                            </Button>
                                                            {pred ? (
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="Edit prediction"
                                                                    onClick={() => setEditPredictionDialog({
                                                                        gameId: pred.id,
                                                                        home_team: pred.home_team,
                                                                        away_team: pred.away_team,
                                                                        home_team_score: pred.home_team_score ?? 0,
                                                                        away_team_score: pred.away_team_score ?? 0
                                                                    })}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            ) : (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => setAddPredictionDialog({
                                                                        home_team: home,
                                                                        away_team: away,
                                                                        home_team_score: 0,
                                                                        away_team_score: 0
                                                                    })}
                                                                >
                                                                    Add
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : adminSelectedMemberId && adminPredictionRound && !loadingAdminFixtures && adminFixturesForRound.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No fixtures for this {isWorldCup ? 'day' : 'week'}.</Typography>
                        ) : adminSelectedMemberId && !adminPredictionRound ? (
                            <Typography variant="body2" color="text.secondary">Select a {isWorldCup ? 'day' : 'game week'} to view or add predictions.</Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Select a member and {isWorldCup ? 'day' : 'game week'} to edit or add missed picks.</Typography>
                        )}
                    </Box>
                )}
            </AdminPanel>
        )}

        {/* Start new season (admin) */}
        <Dialog open={newSeasonDialogOpen} onClose={() => !startingNewSeason && setNewSeasonDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Start new season?</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Resets the league table to 0W/0D/0L for every member. Prior-season picks stay saved but no longer count toward standings.
                    Backfilled scores and weekly week-winner history are cleared. Members, invite link, and league settings are unchanged.
                </Typography>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={newSeasonSyncFixtures}
                            onChange={(e) => setNewSeasonSyncFixtures(e.target.checked)}
                            disabled={startingNewSeason}
                        />
                    )}
                    label={"Sync fixtures for this league's competition (pull in schedule when available)"}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setNewSeasonDialogOpen(false)} disabled={startingNewSeason}>Cancel</Button>
                <Button variant="contained" color="warning" onClick={handleStartNewSeason} disabled={startingNewSeason}>
                    {startingNewSeason ? 'Starting…' : 'Start new season'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Delete league confirmation */}
        <Dialog
            open={deleteLeagueDialog}
            onClose={() => {
                if (!deletingLeague) {
                    setDeleteLeagueDialog(false)
                    setDeleteLeagueError(null)
                }
            }}
        >
            <DialogTitle>Delete league?</DialogTitle>
            <DialogContent>
                <Typography>This will remove the league and all members. This cannot be undone.</Typography>
                {deleteLeagueError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {deleteLeagueError}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteLeagueDialog(false)} disabled={deletingLeague}>
                    Cancel
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    onClick={handleDeleteLeague}
                    disabled={deletingLeague}
                >
                    {deletingLeague ? 'Deleting…' : 'Delete league'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Update your display name in this league */}
        <Dialog open={displayNameDialogOpen} onClose={() => !displayNameSaving && setDisplayNameDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Update display name</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This name is shown on the leaderboard and to other members. It must be unique in this league.
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Display name"
                    fullWidth
                    variant="standard"
                    value={displayNameDraft}
                    onChange={(e) => { setDisplayNameDraft(e.target.value); setDisplayNameError(null); }}
                    placeholder="Your name in this league"
                />
                {displayNameError && <Alert severity="error" sx={{ mt: 2 }}>{displayNameError}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDisplayNameDialogOpen(false)} disabled={displayNameSaving}>Cancel</Button>
                <Button onClick={handleSaveDisplayName} variant="contained" color="primary" disabled={displayNameSaving}>
                    {displayNameSaving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Edit prediction (admin) */}
        <Dialog open={!!editPredictionDialog} onClose={() => setEditPredictionDialog(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Edit prediction</DialogTitle>
            {editPredictionDialog && (
                <>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>{editPredictionDialog.home_team} vs {editPredictionDialog.away_team}</Typography>
                        <TextField
                            label="Home score"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={editPredictionDialog.home_team_score}
                            onChange={(e) => setEditPredictionDialog(prev => ({ ...prev, home_team_score: e.target.value }))}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Away score"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={editPredictionDialog.away_team_score}
                            onChange={(e) => setEditPredictionDialog(prev => ({ ...prev, away_team_score: e.target.value }))}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditPredictionDialog(null)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleAdminUpdatePrediction(
                                editPredictionDialog.gameId,
                                editPredictionDialog.home_team_score,
                                editPredictionDialog.away_team_score
                            )}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>

        {/* Set fixture week/day (admin) */}
        <Dialog open={!!roundOverrideDialog} onClose={() => !savingRoundOverride && setRoundOverrideDialog(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Set {isWorldCup ? 'day' : 'game week'}</DialogTitle>
            {roundOverrideDialog && (
                <>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {roundOverrideDialog.home_team} vs {roundOverrideDialog.away_team}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Current: {roundOverrideDialog.current_round ? weekOrDayTitle(roundOverrideDialog.current_round) : 'Not set'}
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            label={isWorldCup ? 'Day number' : 'Week number'}
                            value={roundOverrideDialog.new_round}
                            onChange={(e) => setRoundOverrideDialog(prev => ({ ...prev, new_round: e.target.value }))}
                            inputProps={{ min: 1 }}
                        />
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={(
                                <Checkbox
                                    checked={!!roundOverrideDialog.manual_round_override}
                                    onChange={(e) => setRoundOverrideDialog(prev => ({ ...prev, manual_round_override: e.target.checked }))}
                                />
                            )}
                            label={`Lock ${isWorldCup ? 'day' : 'week'} override (prevent sync from resetting it)`}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRoundOverrideDialog(null)} disabled={savingRoundOverride}>Cancel</Button>
                        <Button onClick={handleAdminSetFixtureRound} variant="contained" disabled={savingRoundOverride}>
                            {savingRoundOverride ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>

        {/* Add prediction for missed pick (admin) */}
        <Dialog open={!!addPredictionDialog} onClose={() => setAddPredictionDialog(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Add prediction (missed pick)</DialogTitle>
            {addPredictionDialog && (
                <>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>{addPredictionDialog.home_team} vs {addPredictionDialog.away_team}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>Enter the score to record for this member. It will be scored as Win/Draw/Loss if the fixture has results.</Typography>
                        <TextField
                            label="Home score"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={addPredictionDialog.home_team_score}
                            onChange={(e) => setAddPredictionDialog(prev => ({ ...prev, home_team_score: e.target.value }))}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Away score"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={addPredictionDialog.away_team_score}
                            onChange={(e) => setAddPredictionDialog(prev => ({ ...prev, away_team_score: e.target.value }))}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAddPredictionDialog(null)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleAdminCreatePrediction(
                                Number(adminSelectedMemberId),
                                addPredictionDialog.home_team,
                                addPredictionDialog.away_team,
                                addPredictionDialog.home_team_score,
                                addPredictionDialog.away_team_score
                            )}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>

        {/* Backfill standings (admin) */}
        <Dialog open={backfillDialogOpen} onClose={() => { setBackfillDialogOpen(false); setBackfillMessage(null); setBackfillJson(''); }} maxWidth="sm" fullWidth>
            <DialogTitle>Backfill league standings</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Paste JSON with a &quot;standings&quot; array. Each item: display_name (must match a league member), wins, draws, losses, points (optional).
                    Use &quot;Export current&quot; to save a backup or restore previously backfilled scores.
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                    disabled={!leagueId}
                    onClick={() => {
                        if (!leagueId) return
                        authenticatedFetch(`/api/v1/leagues/${leagueId}/backfill-standings`)
                            .then(res => res.ok ? res.json() : res.json().then(err => { throw new Error(err.error || 'Failed to load') }))
                            .then(data => {
                                setBackfillJson(JSON.stringify({ standings: data.standings || [] }, null, 2))
                                setBackfillMessage({ type: 'success', text: 'Current backfill loaded. Save this JSON as a backup or Submit to re-apply.' })
                            })
                            .catch(err => setBackfillMessage({ type: 'error', text: err.message || 'Could not load backfill' }))
                    }}
                >
                    Export current backfill
                </Button>
                <TextField
                    multiline
                    minRows={6}
                    fullWidth
                    placeholder={'{"standings": [{"display_name": "Alice", "wins": 5, "draws": 2, "losses": 1, "points": 17}]}'}
                    value={backfillJson}
                    onChange={(e) => setBackfillJson(e.target.value)}
                    sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                {backfillMessage && (
                    <Alert severity={backfillMessage.type} sx={{ mt: 2 }}>{backfillMessage.text}</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { setBackfillDialogOpen(false); setBackfillMessage(null); setBackfillJson(''); }}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!leagueId || !backfillJson.trim()}
                    onClick={() => {
                        let standings
                        try {
                            const data = JSON.parse(backfillJson.trim())
                            standings = data.standings
                            if (!Array.isArray(standings)) throw new Error('JSON must include "standings" array')
                        } catch (e) {
                            setBackfillMessage({ type: 'error', text: e.message || 'Invalid JSON' })
                            return
                        }
                        authenticatedFetch(`/api/v1/leagues/${leagueId}/backfill-standings`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ standings })
                        })
                            .then(res => res.json().then(data => ({ ok: res.ok, data })))
                            .then(({ ok, data }) => {
                                if (!ok) throw new Error(data.error || 'Request failed')
                                setBackfillMessage({ type: 'success', text: data.message || 'Standings updated.' })
                                fetchLeaderboard()
                            })
                            .catch((err) => setBackfillMessage({ type: 'error', text: err.message || 'Request failed' }))
                    }}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
        </Box>
        </>
      );
}

export default Members;